import { exportDataGrid as exportExcel } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { exportDataGrid as exportPdf } from "devextreme/pdf_exporter";
import { jsPDF } from 'jspdf';
import saveAs from "file-saver";
import Helper from "./helper";
import FormHelper from "./form-helper";
import { useAppStore } from "@/stores/app-store";
import { CustomStore } from "devextreme/common/data";

class GridHelper {
  static summary(column, summaryType) {
    return {
      column,
      summaryType,
      displayFormat: '{0}',
      valueFormat: '#,##0.##;(#,##0.##)'
    }
  }

  static id() {
    return {
      dataField: "id",
      caption: "id",
      visible: false,
    }
  }

  static number(dataField, caption, visible = true) {
    return this.createColumn(dataField, caption, 'number', FormHelper.createNumberBoxOptions(), [], visible)
  }

  static button(icon, text, onClick) {
    return {
      icon,
      text,
      onClick
    }
  }

  static string(dataField, caption, validationRules = [], visible = true, additionalOptions = {}) {
    return {
      dataField,
      dataType: "string",
      caption,
      validationRules,
      visible,
      ...additionalOptions
    };
  }

  static date(dataField = 'tanggal', caption = 'Tanggal') {
    return this.createColumn(dataField, caption, 'date', FormHelper.createDateBoxOptions())
  }

  static no_dokumen(dataField = "no_dokumen", caption = "#Dokumen") {
    return this.createColumn(dataField, caption, 'string')
  }

  static gudang() {
    return this.createColumn('gudang', 'Gudang', 'string')
  }

  static no_ref(visible = false) {
    return { ...this.createColumn('no_ref', 'No Ref', 'string'), visible }
  }

  static keterangan(visible = false) {
    return { ...this.createColumn('keterangan', 'Keterangan', 'string'), visible }
  }

  static noRefKeterangan(visible = false) {
    return [
      this.no_ref(visible),
      this.keterangan(visible)
    ]
  }

  static is_aktif(dataField = 'is_aktif', caption = 'Is Aktif') {
    return {
      dataField,
      caption,
      dataType: "boolean",
    };
  }

  static boolean(dataField, caption, visible = false) {
    return {
      dataField,
      caption,
      dataType: "boolean",
      visible,
    };
  }

  static createdAt(visible = false) {
    return {
      dataField: "created_at",
      dataType: "date",
      caption: "Created",
      format: "yyyy-MM-dd HH:mm:ss",
      visible
    };
  }

  static updatedAt() {
    return {
      dataField: "updated_at",
      dataType: "date",
      caption: "Updated",
      format: "yyyy-MM-dd HH:mm:ss",
    };
  }

  static userAdd(visible = false) {
    return {
      dataField: "user_add",
      dataType: "string",
      caption: "User Add",
      visible
    };
  }

  static userUpdate() {
    return {
      dataField: "user_update",
      dataType: "string",
      caption: "User Update",
    };
  }

  static lookup(dataField, calculateDisplayValue, caption, url, validationRules = [], displayExpr = 'nama', valueExpr = 'id', dataType = "number") {
    return {
      dataField,
      dataType,
      caption,
      calculateDisplayValue,
      lookup: {
        dataSource: Helper.createCustomStoreConfig(url),
        valueExpr,
        displayExpr,
      },
      validationRules
    };
  }

  static createLookupConfig(url, displayExpr = 'nama', valueExpr = 'id') {
    return {
      dataSource: {
        paginate: true,
        pageSize: 30,
        store: new CustomStore(Helper.createCustomStoreConfig(url))
      },
      valueExpr,
      displayExpr,
    }
  }

  static toolbarItem(widget, location, options) {
    return {
      widget,
      location,
      options,
    };
  }

  static buttonColumn(fixed, fixedPosition, buttons) {
    return {
      type: "buttons",
      fixed,
      fixedPosition,
      buttons,
    };
  }

  static onExporting(e, fileName) {
    if (e.format == "xlsx") {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Employees");

      exportExcel({
        component: e.component,
        worksheet: worksheet,
      })
        .then(function () {
          workbook.xlsx.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              fileName + ".xlsx"
            );
          });
        })
        .then(function () {
          e.component.endUpdate();
        });
    } else if (e.format == "pdf") {
      const doc = new jsPDF();
      exportPdf({
        jsPDFDocument: doc,
        component: e.component,
      }).then(() => {
        doc.save(fileName + ".pdf");
      });
    }
  }

  static createSatuanDataSource(barang_id_prop_name = 'barang_id') {
    const store = useAppStore()
    let dataSource = (options) => {
      return {
        load() {
          return new Promise((resolve, reject) => {
            store.post('helper/satuan_barang', { barang_id: options.data[barang_id_prop_name] })
              .then(daf => {
                resolve(daf)
              })
              .catch(e => reject(e));
          })
        },
        byKey(satuan) {
          return new Promise((resolve, reject) => {
            store.post('helper/satuan_barang', { barang_id: options.data[barang_id_prop_name], satuan })
              .then(daf => {
                resolve(daf)
              })
              .catch((e) => reject(e))
          })
        }
      }
    }
    return dataSource;
  }

  static createSatuanLookup(barang_id_prop_name = 'barang_id') {
    let dataSource = this.createSatuanDataSource(barang_id_prop_name)
    return {
      dataSource,
      displayExpr: 'satuan',
      valueExpr: 'satuan',
    }
  }

  static createColumn(dataField, caption, dataType = 'string', editorOptions = {}, validationRules = [], visible = true) {
    let format = null
    if (dataType == 'date') {
      format = 'dd-MM-yyyy HH:mm:ss'
    } else if (dataType == 'number') {
      format = '#,##0.##;(#,##0.##)'
    }
    return {
      dataField,
      caption,
      dataType,
      format,
      editorOptions,
      validationRules,
      visible
    }
  }

  static createDefaultUserAndTimestamp() {
    return [
      this.createColumn('user_add', 'User Add', 'string', FormHelper.createTextBoxOptions(), [], false),
      this.createdAt(),
      this.createColumn('user_update', 'User Update', 'string', FormHelper.createTextBoxOptions(), [], true),
      this.updatedAt(),
    ]
  }

  static total_item(column, summaryType = 'sum', valueFormat = '#,##0.##;(#,##0.##)') {
    return {
      column,
      summaryType,
      displayFormat: '{0}',
      valueFormat
    }
  }

  static qty() {
    return {
      dataField: "qty",
      caption: "Qty",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      validationRules: [FormHelper.createRequiredValidationRule()],
      visible: true
    }
  }

  static satuan(onPilih) {
    return {
      dataField: "satuan",
      caption: "Satuan",
      dataType: "string",
      editorOptions: {
        readOnly: true,
        buttons: [
          {
            name: "cari",
            location: "after",
            options: {
              disabled: false,
              icon: "find",
              onClick: (e) => {
                onPilih(e)
              },
            },
          }
        ]
      },
      validationRules: [FormHelper.createRequiredValidationRule()],
      visible: true
    }
  }

  static isi() {
    return {
      dataField: "isi",
      caption: "Isi",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
      allowEditing: false,
    }
  }

  static harga() {
    return {
      dataField: "harga",
      caption: "Harga",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      validationRules: [FormHelper.createRequiredValidationRule()],
      visible: true
    }
  }

  static disc1() {
    return {
      dataField: "disc1",
      caption: "Disc1",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: true
    }
  }

  static disc2() {
    return {
      dataField: "disc2",
      caption: "Disc2",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: true
    }
  }

  static disc3() {
    return {
      dataField: "disc3",
      caption: "Disc3",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: true
    }
  }

  static disc4() {
    return {
      dataField: "disc4",
      caption: "Disc4",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: true
    }
  }

  static disc5() {
    return {
      dataField: "disc5",
      caption: "Disc5",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: true
    }
  }

  static potonganUnit() {
    return {
      dataField: "potongan_unit",
      caption: "Potongan Unit",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
      calculateCellValue: (data) => {
        return data.potongan / data.qty;
      },
      setCellValue: (newData, value, currentData) => {
        newData.potongan_unit = value;
        newData.potongan = value * currentData.qty;
      }
    }
  }

  static potongan() {
    return {
      dataField: "potongan",
      caption: "Potongan",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorType: 'dxNumberBox',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: false
    }
  }

  static pajak() {
    return {
      dataField: "pajak",
      caption: "Pajak",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: false,
      allowEditing: false,
      calculateCellValue: (data) => {
        return this.hitungPajak(data);
      }
    }
  }

  static hitungPajak(baris) {
    if (baris.is_include) {
      return 0.0;
    } else {
      return this.hitungSubtotalSebelumPajak(baris) * baris.tarif_ppn / 100;
    }
  }

  static hitungSubtotalSebelumPajak(baris) {
    let d4 = parseFloat(baris.disc4 || 0.0);
    let d5 = parseFloat(baris.disc5 || 0.0);
    return baris.qty * baris.harga * (1 - baris.disc1 / 100) * (1 - baris.disc2 / 100) * (1 - baris.disc3 / 100) * (1 - d4 / 100) * (1 - d5 / 100) - baris.potongan
  }

  static hitungSubtotal(baris) {
    console.dir(baris)
    return this.hitungSubtotalSebelumPajak(baris) + this.hitungPajak(baris)
  }

  static subtotal() {
    return {
      dataField: "subtotal",
      caption: "Subtotal",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
      editorOptions: FormHelper.createNumberBoxOptions(),
      calculateCellValue: (data) => {
        return this.hitungSubtotal(data);
      },
      setCellValue: (newData, value, currentData) => {
        newData.subtotal = value;
        newData.harga = value / currentData.qty;
        newData.disc1 = 0.0;
        newData.disc2 = 0.0;
        newData.disc3 = 0.0;
        newData.potongan_unit = 0.0;
        newData.potongan = 0.0;
        newData.pajak = 0.0;
        newData.tarif_ppn = 0.0;
      }
    }
  }

  static hargaNet() {
    return {
      dataField: "harga_net",
      caption: "Harga Net",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
      allowEditing: false,
      calculateCellValue: (data) => {
        return (this.hitungSubtotalSebelumPajak(data) + this.hitungPajak(data)) / (data.qty * data.isi);
      },
    }
  }

  static hargaJual() {
    return {
      dataField: "harga_jual",
      caption: "Harga Jual",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
      editorOptions: FormHelper.createNumberBoxOptions(),
    }
  }

  static isInclude() {
    return {
      dataField: "is_include",
      caption: "Is Include",
      dataType: "boolean",
      visible: false
    }
  }

  static tarifPpn() {
    return {
      dataField: "tarif_ppn",
      caption: "Tarif PPN",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: false
    }
  }

  static hitungDpp(baris) {
    if (baris.is_include) {
      return this.hitungSubtotalSebelumPajak(baris) / (1 + baris.tarif_ppn / 100);
    } else {
      return this.hitungSubtotalSebelumPajak(baris);
    }
  }

  static hitungPpn(baris) {
    return this.hitungDpp(baris) * baris.tarif_ppn / 100;
  }

  static dpp() {
    return {
      dataField: "dpp",
      dataType: "number",
      caption: "DPP",
      format: '#,##0.##;(#,##0.##)',
      visible: false,
      calculateCellValue: (data) => {
        return this.hitungDpp(data);
      },
      allowEditing: false,
    }
  }

  static ppn() {
    return {
      dataField: "ppn",
      dataType: "number",
      caption: "PPN",
      format: '#,##0.##;(#,##0.##)',
      visible: false,
      calculateCellValue: (data) => {
        return this.hitungPpn(data);
      },
      allowEditing: false,
    }
  }

  static batchNo() {
    return {
      dataField: "batch_no",
      dataType: "string",
      caption: "Batch No",
      editorOptions: FormHelper.createTextBoxOptions(),
      visible: false
    }
  }

  static expDate() {
    return {
      dataField: "exp_date",
      dataType: "date",
      caption: "Exp Date",
      format: "dd-MM-yyyy HH:mm:ss",
      editorOptions: FormHelper.createDateBoxOptions(),
      visible: false
    }
  }

  static fixCost() {
    return {
      dataField: "fix_cost",
      dataType: "number",
      caption: "Fix Cost / unit",
      format: '#,##0.##;(#,##0.##)',
      visible: false,
      editorOptions: FormHelper.createNumberBoxOptions(),
    }
  }

  static ongkir() {
    return {
      dataField: "ongkir",
      caption: "Ongkir / unit",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      editorOptions: FormHelper.createNumberBoxOptions(),
      visible: false
    }
  }

  static barang(store) {
    return {
      dataField: 'barang_id',
      dataType: 'number',
      calculateDisplayValue: 'barang',
      caption: 'Barang',
      lookup: GridHelper.createLookupConfig("helper/barang"),
      validationRules: [FormHelper.createRequiredValidationRule()],
      alignment: 'left',
      setCellValue: (newData, value) => {
        return new Promise((resolve, reject) => {
          store.post('helper/barang', { id: value })
            .then(data => {
              newData.barang_id = value
              newData.sku = data.sku
              newData.barang = data.nama
              newData.satuan = data.satuan
              newData.isi = 1.0
              newData.harga = parseFloat(data.last_beli_harga || 0.0)
              newData.disc1 = data.last_beli_disc1 || 0.0
              newData.disc2 = data.last_beli_disc2 || 0.0
              newData.disc3 = data.last_beli_disc3 || 0.0
              newData.disc4 = data.last_beli_disc4 || 0.0
              newData.disc5 = data.last_beli_disc5 || 0.0
              newData.potongan = data.last_beli_potongan || 0.0
              newData.tarif_ppn = data.last_beli_tarif_ppn || 0.0
              newData.is_include = data.last_beli_is_include || false
              resolve()
            })
            .catch(e => reject(e))
        });
      }
    }
  }

  static sku() {
    return {
      dataField: 'sku',
      dataType: 'string',
      caption: 'SKU',
      allowEditing: false,
      visible: false
    }
  }

  static nama(dataField = 'nama', caption = 'Nama') {
    return {
      dataField,
      dataType: 'string',
      caption,
      allowEditing: false,
      visible: true
    }
  }

  static pin(dataField = 'pin', caption = 'pin') {
    return {
      dataField,
      dataType: 'string',
      caption,
      allowEditing: false,
      visible: true
    }
  }

  static findEditingRow(grid) {
    let rows = grid.getVisibleRows();
    return rows.find(row => row.isEditing);
  }

  static kategori_barang() {
    return {
      dataField: "kategori_barang",
      caption: "Kategori Barang",
      dataType: "string",
      visible: true,
    }
  }

  static kategori_pelanggan() {
    return {
      dataField: "kategori_pelanggan",
      caption: "Kategori Pelanggan",
      dataType: "string",
      visible: true,
    }
  }

  static kota() {
    return {
      dataField: "kota",
      caption: "Kota",
      dataType: "string",
      visible: true,
    }
  }

  static telepon() {
    return {
      dataField: "telepon",
      caption: "Telepon",
      dataType: "string",
      visible: true,
    }
  }

  static departemen() {
    return {
      dataField: "departemen",
      caption: "Departemen",
      dataType: "string",
      visible: false,
    }
  }

  static brand() {
    return {
      dataField: "brand",
      caption: "Brand",
      dataType: "string",
      visible: true,
    }
  }

  static stok() {
    return {
      dataField: "stok",
      caption: "Stok",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
    }
  }

  // UAT #1452: Daftar Barang shows stok habis/minus (stok <= 0) as a red Stok cell, and stok
  // menipis (0 < stok <= stok_minimum of the row) as an amber one. A row without stok_minimum
  // (NULL/0, or a page whose API does not send it) is never menipis.
  // A cellTemplate, not onCellPrepared, so it works in every barang page variant (my-grid and
  // my-remote-grid forward it); export reads the value, not the template.
  static tandaiStokHabis(column) {
    return {
      ...column,
      cellTemplate(container, options) {
        container.textContent = options.text;
        const stok = options.value;
        const minimum = Number(options.data?.stok_minimum) || 0;
        if (stok != null && stok <= 0) {
          container.classList.add("stok-habis");
          container.style.color = "#c62828";
          container.style.backgroundColor = "#fdecea";
          container.style.fontWeight = "600";
        } else if (stok != null && minimum > 0 && stok <= minimum) {
          container.classList.add("stok-menipis");
          container.style.color = "#8a5a00";
          container.style.backgroundColor = "#fff4d6";
          container.style.fontWeight = "600";
        }
      },
    };
  }

  static stok_minimum() {
    return {
      dataField: "stok_minimum",
      caption: "Stok Minimum",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
    }
  }

  // UAT #1452: "Habis" / "Menipis" / "" from barang/daftar, filtered by the Stok quick filter.
  static status_stok() {
    return {
      dataField: "status_stok",
      caption: "Status Stok",
      dataType: "string",
      visible: false,
    }
  }

  // UAT #1452: toolbar quick filter for Daftar Barang; getGrid returns the dxDataGrid instance.
  static filterCepatStok(getGrid) {
    const pilihan = {
      "Semua stok": undefined,
      "Stok menipis": ["Menipis"],
      "Stok habis/minus": ["Habis"],
      "Menipis + habis": ["Menipis", "Habis"],
    };
    return {
      location: "before",
      widget: "dxSelectBox",
      options: {
        items: Object.keys(pilihan),
        value: "Semua stok",
        width: 170,
        elementAttr: { class: "filter-cepat-stok" },
        onValueChanged(e) {
          getGrid().columnOption("status_stok", "filterValues", pilihan[e.value]);
        },
      },
    };
  }

  static harga_jual() {
    return {
      dataField: "harga_jual",
      caption: "Harga Jual",
      dataType: "number",
      format: '#,##0.##;(#,##0.##)',
      visible: true,
    }
  }

  static is_jasa() {
    return {
      dataField: "is_jasa",
      caption: "Is Jasa",
      dataType: "boolean",
      visible: true,
    }
  }

  static gridLoadState(key) {
    let state = JSON.parse(localStorage.getItem(key));
    if (!state) {
      return {}
    }
    if (state.selectedRowKeys) {
      delete state.selectedRowKeys
    }
    return state
  }

  static gridSaveState(key, state) {
    if (state.selectedRowKeys) {
      delete state.selectedRowKeys
    }
    localStorage.setItem(key, JSON.stringify(state));
  }
}

export default GridHelper;
